import { useEffect, useState } from "react";
import api, { money, apiErr, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Building2, Plus, Pencil, Trash2, Search, MapPin, History, Star, PackageCheck, Upload, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const blank = { name: "", address: "", work_description: "", lorry_numbers: "", state: "", city: "", phone: "", gst_number: "", pan_number: "", aadhar_number: "", gst_certificate: "", pan_card: "", aadhar_front: "", aadhar_back: "" };

const fileUrl = (path) => `${API}/files/${path}?auth=${localStorage.getItem("arrow_token") || ""}`;

export default function VendorsPage() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "manager";
  const [vendors, setVendors] = useState([]);
  const [locs, setLocs] = useState({ states: [], cities: [] });
  const [fState, setFState] = useState("all");
  const [fCity, setFCity] = useState("all");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [histFor, setHistFor] = useState(null);
  const [hist, setHist] = useState(null);
  const [kycFor, setKycFor] = useState(null);
  const [kycStatus, setKycStatus] = useState("pending");
  const [kycExpiry, setKycExpiry] = useState("");
  const openKyc = (v) => { setKycFor(v); setKycStatus(v.kyc_status || "pending"); setKycExpiry(v.kyc_expiry || ""); };
  const saveKyc = async () => {
    try {
      await api.patch(`/vendors/${kycFor.id}/kyc`, { kyc_status: kycStatus, kyc_expiry: kycExpiry });
      toast.success("KYC status updated");
      setKycFor(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const load = () => {
    const p = new URLSearchParams();
    if (fState !== "all") p.set("state", fState);
    if (fCity !== "all") p.set("city", fCity);
    if (search) p.set("search", search);
    api.get(`/vendors?${p}`).then((r) => setVendors(r.data));
  };
  useEffect(() => { api.get("/vendors/locations").then((r) => setLocs(r.data)); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fState, fCity, search]);

  const openNew = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (v) => { setEditing(v); setForm({ ...v }); setOpen(true); };
  const save = async () => {
    try {
      if (editing) await api.put(`/vendors/${editing.id}`, form);
      else await api.post("/vendors", form);
      toast.success(editing ? "Vendor updated" : "Vendor added");
      setOpen(false); api.get("/vendors/locations").then((r) => setLocs(r.data)); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (v) => { if (!window.confirm(`Delete ${v.name}?`)) return; await api.delete(`/vendors/${v.id}`); toast.success("Deleted"); load(); };

  const openHistory = async (v) => {
    setHistFor(v); setHist(null);
    try { const r = await api.get(`/vendors/${v.id}/history`); setHist(r.data); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-primary">Network</div>
          <h1 className="font-heading font-black text-4xl tracking-tight">Logistics Partners</h1>
        </div>
        {canEdit && <Button onClick={openNew} className="rounded-none gap-2 font-bold uppercase tracking-wide" data-testid="add-vendor-btn"><Plus className="h-4 w-4" /> Add vendor</Button>}
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="rounded-none pl-9" placeholder="Search partners…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="vendor-search" />
        </div>
        <Select value={fState} onValueChange={setFState}>
          <SelectTrigger className="rounded-none w-44" data-testid="filter-state"><SelectValue placeholder="State" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All states</SelectItem>{locs.states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={fCity} onValueChange={setFCity}>
          <SelectTrigger className="rounded-none w-44" data-testid="filter-city"><SelectValue placeholder="City" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All cities</SelectItem>{locs.cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="vendors-table">
          <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
            <th className="py-3 px-4">Partner</th><th className="px-4">Work</th><th className="px-4">Lorries</th><th className="px-4">Location</th><th className="px-4">Phone</th><th className="px-4 text-right">Actions</th>
          </tr></thead>
          <tbody>
            {vendors.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">No partners found.</td></tr>}
            {vendors.map((v) => (
              <tr key={v.id} className="border-b border-border last:border-0 hover:bg-muted transition-colors" data-testid={`vendor-row-${v.id}`}>
                <td className="py-3 px-4"><div className="font-medium flex items-center gap-2 flex-wrap">{v.name} <KycBadge v={v} /></div><div className="text-xs text-muted-foreground">{v.address}</div></td>
                <td className="px-4 text-muted-foreground max-w-[200px] truncate">{v.work_description}</td>
                <td className="px-4 font-mono-num text-xs">{v.lorry_numbers}</td>
                <td className="px-4"><span className="inline-flex items-center gap-1 text-xs"><MapPin className="h-3 w-3 text-primary" />{v.city}, {v.state}</span></td>
                <td className="px-4 font-mono-num text-xs">{v.phone}</td>
                <td className="px-4 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" className="rounded-none gap-1 text-xs" onClick={() => openHistory(v)} data-testid={`vendor-history-${v.id}`}><History className="h-4 w-4" /> History</Button>
                  <Button size="sm" variant="ghost" className="rounded-none gap-1 text-xs" onClick={() => openKyc(v)} data-testid={`vendor-kyc-${v.id}`}><ShieldCheck className="h-4 w-4" /> KYC</Button>
                  {canEdit && <>
                    <Button size="sm" variant="ghost" className="rounded-none" onClick={() => openEdit(v)} data-testid={`edit-vendor-${v.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" className="rounded-none text-destructive" onClick={() => del(v)} data-testid={`delete-vendor-${v.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-lg" data-testid="vendor-dialog">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight">{editing ? "Edit vendor" : "Add vendor"}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
            <F label="Vendor name" v={form.name} on={(x) => setForm({ ...form, name: x })} id="name" />
            <div className="space-y-2"><Label>Address</Label><Textarea className="rounded-none" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="vendor-address" /></div>
            <div className="space-y-2"><Label>Work description</Label><Textarea className="rounded-none" value={form.work_description} onChange={(e) => setForm({ ...form, work_description: e.target.value })} data-testid="vendor-work" /></div>
            <F label="Registered lorry numbers" v={form.lorry_numbers} on={(x) => setForm({ ...form, lorry_numbers: x })} id="lorries" ph="MH12AB1234, KA05CD5678" />
            <div className="grid grid-cols-2 gap-3">
              <F label="Operational state" v={form.state} on={(x) => setForm({ ...form, state: x })} id="state" />
              <F label="Operational city" v={form.city} on={(x) => setForm({ ...form, city: x })} id="city" />
            </div>
            <F label="Phone number" v={form.phone} on={(x) => setForm({ ...form, phone: x })} id="phone" />

            <div className="border-t border-border pt-3 mt-1">
              <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-primary mb-3"><ShieldCheck className="h-4 w-4" /> KYC Verification</div>
              <div className="space-y-3">
                <div className="grid grid-cols-[1fr_auto] gap-2 items-end">
                  <F label="GST Number" v={form.gst_number} on={(x) => setForm({ ...form, gst_number: x })} id="gst_number" />
                  <DocUpload label="GST Certificate" path={form.gst_certificate} onChange={(p) => setForm({ ...form, gst_certificate: p })} testid="gst_certificate" />
                </div>
                <div className="grid grid-cols-[1fr_auto] gap-2 items-end">
                  <F label="PAN Number" v={form.pan_number} on={(x) => setForm({ ...form, pan_number: x })} id="pan_number" />
                  <DocUpload label="PAN Card" path={form.pan_card} onChange={(p) => setForm({ ...form, pan_card: p })} testid="pan_card" />
                </div>
                <F label="Aadhar Number" v={form.aadhar_number} on={(x) => setForm({ ...form, aadhar_number: x })} id="aadhar_number" />
                <div className="grid grid-cols-2 gap-2">
                  <DocUpload label="Aadhar Front" path={form.aadhar_front} onChange={(p) => setForm({ ...form, aadhar_front: p })} testid="aadhar_front" />
                  <DocUpload label="Aadhar Back" path={form.aadhar_back} onChange={(p) => setForm({ ...form, aadhar_back: p })} testid="aadhar_back" />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter><Button onClick={save} className="rounded-none font-bold uppercase tracking-wide w-full" data-testid="save-vendor">{editing ? "Save changes" : "Add vendor"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!histFor} onOpenChange={(o) => { if (!o) { setHistFor(null); setHist(null); } }}>
        <DialogContent className="rounded-none max-w-2xl" data-testid="vendor-history-dialog">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight">{histFor?.name} — Order History</DialogTitle></DialogHeader>
          {!hist ? (
            <div className="py-10 text-center text-muted-foreground">Loading…</div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-px bg-border border border-border">
                <div className="bg-card p-4" data-testid="vendor-lifetime-orders">
                  <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-wide text-muted-foreground">Lifetime Orders</span><History className="h-4 w-4 text-muted-foreground" /></div>
                  <div className="font-mono-num font-bold text-3xl mt-2">{hist.lifetime_orders ?? 0}</div>
                </div>
                <div className="bg-card p-4" data-testid="vendor-total-orders">
                  <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-wide text-muted-foreground">Completed Orders</span><PackageCheck className="h-4 w-4 text-primary" /></div>
                  <div className="font-mono-num font-bold text-3xl mt-2">{hist.total_orders_done}</div>
                </div>
                <div className="bg-card p-4" data-testid="vendor-avg-rating">
                  <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-wide text-muted-foreground">Average Rating</span><Star className="h-4 w-4 text-amber-400" /></div>
                  <div className="flex items-center gap-2 mt-2">
                    <div className="font-mono-num font-bold text-3xl">{hist.average_rating ?? "—"}</div>
                    {hist.average_rating != null && <Stars value={Math.round(hist.average_rating)} />}
                    <span className="text-xs text-muted-foreground">({hist.rating_count})</span>
                  </div>
                </div>
              </div>
              <div className="border border-border bg-card overflow-x-auto max-h-80 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-muted"><tr className="border-b border-border text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                    <th className="py-2 px-3">Delivered</th><th className="px-3">Order</th><th className="px-3">Route</th><th className="px-3 text-right">Value</th><th className="px-3">Rating</th>
                  </tr></thead>
                  <tbody>
                    {hist.orders.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">No completed orders yet.</td></tr>}
                    {hist.orders.map((o) => (
                      <tr key={o.order_number} className="border-b border-border last:border-0" data-testid={`vendor-order-${o.order_number}`}>
                        <td className="px-3 py-2 text-xs whitespace-nowrap">{o.delivered_at ? new Date(o.delivered_at).toLocaleDateString() : "—"}</td>
                        <td className="px-3 font-mono-num text-primary text-xs">{o.order_number}</td>
                        <td className="px-3 text-xs">{o.route}</td>
                        <td className="px-3 text-right font-mono-num text-xs">{money(o.total)}</td>
                        <td className="px-3">{o.rating ? <Stars value={o.rating} /> : <span className="text-xs text-muted-foreground">No feedback</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!kycFor} onOpenChange={(o) => { if (!o) setKycFor(null); }}>
        <DialogContent className="rounded-none max-w-lg" data-testid="vendor-kyc-dialog">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight">{kycFor?.name} — KYC Documents</DialogTitle></DialogHeader>
          {kycFor && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div><div className="text-[11px] uppercase text-muted-foreground">GST No.</div><div className="font-mono-num">{kycFor.gst_number || "—"}</div></div>
                <div><div className="text-[11px] uppercase text-muted-foreground">PAN No.</div><div className="font-mono-num">{kycFor.pan_number || "—"}</div></div>
                <div><div className="text-[11px] uppercase text-muted-foreground">Aadhar No.</div><div className="font-mono-num">{kycFor.aadhar_number || "—"}</div></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <DocThumb label="GST Certificate" path={kycFor.gst_certificate} />
                <DocThumb label="PAN Card" path={kycFor.pan_card} />
                <DocThumb label="Aadhar Front" path={kycFor.aadhar_front} />
                <DocThumb label="Aadhar Back" path={kycFor.aadhar_back} />
              </div>
              {canEdit && (
                <div className="border-t border-border pt-4 grid sm:grid-cols-3 gap-3 items-end" data-testid="kyc-admin-controls">
                  <div className="space-y-1">
                    <Label className="text-xs">Verification</Label>
                    <div className="flex items-center gap-2 h-10">
                      <Switch checked={kycStatus === "verified"} onCheckedChange={(on) => setKycStatus(on ? "verified" : "pending")} data-testid="kyc-verify-toggle" />
                      <span className={`text-sm font-bold ${kycStatus === "verified" ? "text-emerald-600" : "text-amber-600"}`}>{kycStatus === "verified" ? "Verified" : "Pending"}</span>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Documents expiry</Label>
                    <Input type="date" className="rounded-none" value={kycExpiry} onChange={(e) => setKycExpiry(e.target.value)} data-testid="kyc-expiry" />
                  </div>
                  <Button className="rounded-none font-bold uppercase text-xs" onClick={saveKyc} data-testid="save-kyc">Save KYC</Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KycBadge({ v }) {
  const expired = v.kyc_expiry && new Date(v.kyc_expiry) < new Date();
  const verified = v.kyc_status === "verified" && !expired;
  const label = expired ? "KYC Expired" : verified ? "KYC Verified" : "KYC Pending";
  const cls = expired
    ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
    : verified
      ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
      : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase ${cls}`} data-testid={`kyc-badge-${v.id}`}>
      <ShieldCheck className="h-3 w-3" />{label}
    </span>
  );
}

function DocThumb({ label, path }) {
  const isPdf = path && path.toLowerCase().endsWith(".pdf");
  return (
    <div className="space-y-1">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      {path ? (
        <a href={fileUrl(path)} target="_blank" rel="noreferrer" className="block border border-border h-32 overflow-hidden bg-muted" data-testid={`kyc-thumb-${label.replace(/\s/g, "-").toLowerCase()}`}>
          {isPdf ? <div className="h-full w-full grid place-items-center text-xs font-bold text-primary">View PDF ↗</div> : <img src={fileUrl(path)} alt={label} className="h-full w-full object-contain" />}
        </a>
      ) : (
        <div className="border border-dashed border-border h-32 grid place-items-center text-xs text-muted-foreground">Not uploaded</div>
      )}
    </div>
  );
}

function Stars({ value }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`h-3.5 w-3.5 ${n <= value ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40"}`} />)}
    </span>
  );
}

const F = ({ label, v, on, id, ph }) => (
  <div className="space-y-2"><Label>{label}</Label><Input className="rounded-none" value={v} placeholder={ph} onChange={(e) => on(e.target.value)} data-testid={`vendor-${id}`} /></div>
);

function DocUpload({ label, path, onChange, testid }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.path);
      toast.success(`${label} uploaded`);
    } catch (err) { toast.error(apiErr(err)); }
    finally { setBusy(false); }
  };
  const isPdf = path && path.toLowerCase().endsWith(".pdf");
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <div className="flex items-center gap-2">
        {path ? (
          isPdf
            ? <a href={fileUrl(path)} target="_blank" rel="noreferrer" className="h-11 w-11 shrink-0 border border-border grid place-items-center text-[9px] font-bold text-primary" data-testid={`thumb-${testid}`}>PDF</a>
            : <a href={fileUrl(path)} target="_blank" rel="noreferrer" className="h-11 w-11 shrink-0 border border-border overflow-hidden" data-testid={`thumb-${testid}`}><img src={fileUrl(path)} alt={label} className="h-full w-full object-cover" /></a>
        ) : (
          <div className="h-11 w-11 shrink-0 border border-dashed border-border grid place-items-center text-muted-foreground"><Upload className="h-4 w-4" /></div>
        )}
        <label className="cursor-pointer text-xs border border-border px-3 py-2 hover:bg-primary hover:text-primary-foreground transition-colors whitespace-nowrap" data-testid={`upload-${testid}`}>
          {busy ? "Uploading…" : path ? "Replace" : "Upload"}
          <input type="file" accept="image/*,application/pdf" className="hidden" onChange={upload} disabled={busy} />
        </label>
      </div>
    </div>
  );
}
